import React, { useEffect, useMemo } from "react";

import { Auth } from "shared/@auth/Auth";
import { TabContainer, Tab } from "shared/TabContainer";

import Addresses from "./Addresses";
import Amway from "./Amway";
import Contacts from "./Contacts";
import CreditCards from "./CreditCards";
import DreamCast from "./DreamCast/DreamCast";
import DreamStream from "./DreamStream";
import ThePulse from "./ThePulse";
import Topics from "./Topics";
import IBOLogin from "./IBOLogin";
import Kate from "./Kate";
import Sales from "./Sales/index";
import Notes from "./Notes";
import Emails from "./Emails";
import PasswordReset from "./PasswordReset";
import Incidents from "./Incidents";
import { ResetState } from "./PasswordReset/useResetState";
import { useEmails } from "./Emails/useEmails";
import { IBOInfo } from "../../useIBOInfo";

import { ENVIRONMENT_CONFIG } from "shared/globals";

const { supportsDreamStream, shouldShowKateTab } = ENVIRONMENT_CONFIG.flags;

/**
 * A map of names to query param values.
 */
export const tabs = {
  incidents: "incidents",
  address: "address",
  amway: "amway",
  contacts: "contacts",
  creditCards: "creditcards",
  dreamcast: "dreamcast",
  dreamstream: "dreamstream",
  pulse: "pulse",
  topics: "topics",
  emails: "emails",
  iboLogin: "ibologin",
  kate: "kate",
  notes: "notes",
  passwordReset: "passwordreset",
  sales: "sales",
};

export default function ({ iboNumber, selectedTab, onTabChange, membership }) {
  const { hasPermission } = Auth.useContainer();

  const canSeeIboLogin = hasPermission("ibologin.*");
  const canSeePulse = hasPermission("events.admin");
  const canSeePasswordReset = false && hasPermission("membership.password.reset.*");
  const canSeeDreamStream = hasPermission("membership.dreamstream.read");
  const canSeeDreamCast = hasPermission("membership.dreamcast.read");
  const canSeeTopics = hasPermission("developer.*");
  const canSeeSales = hasPermission("membership.transaction.read");
  const canSeeAmway = hasPermission("membership.amway.read");

  const visibleTabValues = useMemo(() => {
    const values = [tabs.contacts];

    if (canSeeIboLogin) values.push(tabs.iboLogin);
    if (canSeePulse) values.push(tabs.pulse);
    if (canSeePasswordReset) values.push(tabs.passwordReset);
    if (canSeeDreamStream) values.push(tabs.dreamstream);
    if (canSeeDreamCast) values.push(tabs.dreamcast);
    if (canSeeTopics) values.push(tabs.topics);
    if (canSeeSales) values.push(tabs.sales);
    if (shouldShowKateTab) values.push(tabs.kate);

    values.push(tabs.emails);
    values.push(tabs.address);
    values.push(tabs.incidents);
    values.push(tabs.notes);
    values.push(tabs.creditCards);

    if (canSeeAmway) values.push(tabs.amway);

    return values;
  }, [
    canSeeIboLogin,
    canSeePulse,
    canSeePasswordReset,
    canSeeDreamStream,
    canSeeDreamCast,
    canSeeTopics,
    canSeeSales,
    canSeeAmway,
  ]);

  const hasExplicitSelection = Boolean(selectedTab);
  const hasValidSelection = visibleTabValues.includes(selectedTab);
  const normalizedTab = hasValidSelection ? selectedTab : visibleTabValues[0];

  const { refetch: refetchEmails } = useEmails(iboNumber);
  const { refreshIBOInfo } = IBOInfo.useContainer();

  useEffect(() => {
    const scrollable = document.getElementById("scrollableDiv");
    if (!scrollable) {
      return;
    }

    if (normalizedTab === tabs.pulse) {
      scrollable.classList.add("pulseScrollGutter");
    } else {
      scrollable.classList.remove("pulseScrollGutter");
    }

    return () => {
      scrollable.classList.remove("pulseScrollGutter");
    };
  }, [normalizedTab]);

  useEffect(() => {
    if (!hasExplicitSelection || hasValidSelection || !onTabChange) {
      return;
    }

    onTabChange(normalizedTab, { replace: true });
  }, [hasExplicitSelection, hasValidSelection, normalizedTab, onTabChange]);

  // NOTE: All tabs below that don't have permission checks only require this section's top level permissions.

  return (
    <TabContainer value={normalizedTab} onTabChange={onTabChange}>
      <Tab title="Contacts" value={tabs.contacts}>
        <Contacts iboNumber={iboNumber} refetchEmails={refetchEmails} />
      </Tab>

      {canSeeIboLogin && (
        <Tab title="Log in as IBO" value={tabs.iboLogin}>
          <IBOLogin iboNumber={iboNumber} />
        </Tab>
      )}

      {canSeePulse && (
        <Tab title="The Pulse" value={tabs.pulse}>
          <ThePulse iboNumber={iboNumber} membership={membership} />
        </Tab>
      )}

      {/* Password reset feature is no longer used; tab hidden. */}
      {canSeePasswordReset && (
        <Tab title="Password Reset" value={tabs.passwordReset}>
          <ResetState.Provider>
            <PasswordReset iboNumber={iboNumber} />
          </ResetState.Provider>
        </Tab>
      )}

      {canSeeDreamStream && (
        <Tab title="DreamStream&#8482;" value={tabs.dreamstream}>
          <DreamStream iboNumber={iboNumber} membership={membership} />
        </Tab>
      )}

      {canSeeDreamCast && (
        <Tab title="DreamCast&#8482;" value={tabs.dreamcast}>
          <DreamCast iboNumber={iboNumber} />
        </Tab>
      )}

      {canSeeTopics && (
        <Tab title="Topics&#8482;" value={tabs.topics}>
          <Topics iboNumber={iboNumber} membership={membership} />
        </Tab>
      )}

      {canSeeSales && (
        <Tab title="Sales" value={tabs.sales}>
          <Sales iboNumber={iboNumber} />
        </Tab>
      )}

      {shouldShowKateTab && (
        <Tab title="Kate" value={tabs.kate}>
          <Kate iboNumber={iboNumber} />
        </Tab>
      )}

      <Tab title="Emails" value={tabs.emails}>
        <Emails iboNumber={iboNumber} refetchContacts={refreshIBOInfo} />
      </Tab>

      <Tab title="Addresses" value={tabs.address}>
        <Addresses iboNumber={iboNumber} />
      </Tab>

      <Tab title="Incidents" value={tabs.incidents}>
        <Incidents iboNumber={iboNumber} />
      </Tab>

      <Tab title="Notes" value={tabs.notes}>
        <Notes iboNumber={iboNumber} />
      </Tab>

      <Tab title="Credit Cards" value={tabs.creditCards}>
        <CreditCards iboNumber={iboNumber} />
      </Tab>

      {canSeeAmway && (
        <Tab title="Amway" value={tabs.amway}>
          <Amway iboNumber={iboNumber} />
        </Tab>
      )}
    </TabContainer>
  );
}
